import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAudioPlayer } from "@/context/AudioContext";
import { radioStations, type RadioStation } from "@/mocks/radios";
import { programs, type Program } from "@/mocks/programs";
import {
  technicalStreamStatusShortLabels,
  technicalStreamStatusIcons,
  technicalStreamStatusMessages,
  isTransientStatus,
  isTerminalStatus,
  type TechnicalStreamStatus,
} from "@/types/streaming";

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/** Get program linked to a station */
function findProgramForStation(stationId: number): Program | undefined {
  return programs.find((p) => p.radioId === stationId && p.isActive);
}

/** Get all live stations for prev/next cycling */
function getLiveStations(): RadioStation[] {
  return radioStations.filter((s) => s.isLive && s.technicalStreamStatus === "active");
}

export default function TvPlayerPage() {
  const navigate = useNavigate();
  const { radioId } = useParams<{ radioId: string }>();
  const {
    currentStation,
    isPlaying,
    volume,
    stations,
    play,
    toggle,
    stop,
    setVolume,
    changeStation,
    fallbackInfo,
    streamError,
    dismissStreamError,
  } = useAudioPlayer();

  const [focusedBtn, setFocusedBtn] = useState<string>("play");
  const [showVolume, setShowVolume] = useState(false);
  const [hasAutoPlayed, setHasAutoPlayed] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);

  const buttons = ["back", "prev", "play", "next", "fav", "profile"] as const;

  // ── Load favorite from localStorage ──
  useEffect(() => {
    const favs = JSON.parse(localStorage.getItem("rfw_tv_favorites") || "[]");
    if (currentStation) {
      setIsFavorited(favs.includes(currentStation.id));
    }
  }, [currentStation]);

  const toggleFavorite = useCallback(() => {
    if (!currentStation) return;
    const favs: number[] = JSON.parse(localStorage.getItem("rfw_tv_favorites") || "[]");
    let next: number[];
    if (favs.includes(currentStation.id)) {
      next = favs.filter((id: number) => id !== currentStation.id);
    } else {
      next = [...favs, currentStation.id];
    }
    localStorage.setItem("rfw_tv_favorites", JSON.stringify(next));
    setIsFavorited(!isFavorited);
  }, [currentStation, isFavorited]);

  // ── Track recent stations ──
  useEffect(() => {
    if (!currentStation) return;
    const recents: number[] = JSON.parse(localStorage.getItem("rfw_tv_recents") || "[]");
    const filtered = recents.filter((id: number) => id !== currentStation.id);
    const next = [currentStation.id, ...filtered].slice(0, 10);
    localStorage.setItem("rfw_tv_recents", JSON.stringify(next));
  }, [currentStation]);

  // Auto-play station from URL param
  useEffect(() => {
    if (hasAutoPlayed || !radioId) return;
    const numericId = Number(radioId);
    if (isNaN(numericId)) return;
    const targetStation = radioStations.find((s) => s.id === numericId);
    if (targetStation) {
      setHasAutoPlayed(true);
      play(targetStation);
    }
  }, [radioId, hasAutoPlayed, play]);

  // Determine display radio
  const displayStation: RadioStation | null = currentStation || radioStations.find((s) => s.id === 0) || null;
  const displayProgram = displayStation ? findProgramForStation(displayStation.id) : undefined;
  const isFallback = !!fallbackInfo;
  const hasStreamError = !!streamError;
  const isLoading = !currentStation && !fallbackInfo && !streamError;

  // Technical status for current station
  const techStatus: TechnicalStreamStatus = displayStation?.technicalStreamStatus || "active";
  const isTransient = isTransientStatus(techStatus);
  const isTerminal = isTerminalStatus(techStatus);
  const techIcon = technicalStreamStatusIcons[techStatus];
  const techLabel = technicalStreamStatusShortLabels[techStatus];
  const techMessage = technicalStreamStatusMessages[techStatus];

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      const allLive = getLiveStations();

      if (e.key === "ArrowRight") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        const next = (idx + 1) % buttons.length;
        setFocusedBtn(buttons[next]);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        const prev = (idx - 1 + buttons.length) % buttons.length;
        setFocusedBtn(buttons[prev]);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setShowVolume(true);
        setVolume(Math.min(volume + 5, 100));
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setShowVolume(true);
        setVolume(Math.max(volume - 5, 0));
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        navigate("/tv-app");
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedBtn === "play") {
          toggle();
        } else if (focusedBtn === "back") {
          stop();
          navigate("/tv-app");
        } else if (focusedBtn === "prev") {
          if (allLive.length > 0 && currentStation) {
            const curIdx = allLive.findIndex((s) => s.id === currentStation.id);
            const prev = curIdx > 0 ? allLive[curIdx - 1] : allLive[allLive.length - 1];
            changeStation(prev);
          }
        } else if (focusedBtn === "next") {
          if (allLive.length > 0 && currentStation) {
            const curIdx = allLive.findIndex((s) => s.id === currentStation.id);
            const next = curIdx < allLive.length - 1 ? allLive[curIdx + 1] : allLive[0];
            changeStation(next);
          }
        } else if (focusedBtn === "fav") {
          toggleFavorite();
        } else if (focusedBtn === "profile") {
          if (displayStation) navigate(`/tv-app/radio/${displayStation.id}`);
        }
      } else if (e.key === " ") {
        e.preventDefault();
        toggle();
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [focusedBtn, volume, navigate, toggle, setVolume, currentStation, changeStation, stop, toggleFavorite, displayStation]);

  // Hide volume indicator after 2s
  useEffect(() => {
    if (showVolume) {
      const t = setTimeout(() => setShowVolume(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showVolume, volume]);

  // ── Loading State ──
  if (isLoading) {
    return (
      <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-6 animate-pulse">
            <i className="ri-radio-line text-white/40 text-2xl"></i>
          </div>
          <p className="text-white/40 text-lg">Cargando RFW TV...</p>
        </div>
      </div>
    );
  }

  // ── Stream Error State ──
  if (hasStreamError && streamError) {
    return (
      <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none flex items-center justify-center">
        <div className="text-center max-w-lg px-8">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-red-500/10 flex items-center justify-center mb-6">
            <i className="ri-error-warning-line text-red-400 text-3xl"></i>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">No se puede reproducir</h1>
          <p className="text-white/60 text-base mb-2">{streamError.stationName}</p>
          <p className="text-white/40 text-sm mb-8">{streamError.message}</p>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => { dismissStreamError(); navigate("/tv-app"); }}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer transition-all outline-none focus:ring-2 focus:ring-white"
            >
              <i className="ri-arrow-left-line"></i>
              Volver
            </button>
            <button
              onClick={() => { dismissStreamError(); }}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white hover:bg-white/90 text-[#0a0a0f] font-semibold cursor-pointer transition-all outline-none focus:ring-2 focus:ring-white"
            >
              <i className="ri-refresh-line"></i>
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!displayStation) return null;

  return (
    <div className="relative min-h-screen bg-[#0a0a0f] text-white overflow-hidden select-none">
      {/* Full-screen Cover Background */}
      <div className="absolute inset-0">
        <img
          src={displayStation.logoUrl}
          alt={displayStation.name}
          className="w-full h-full object-cover object-top opacity-30 blur-sm scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/60 to-[#0a0a0f]/40"></div>
      </div>

      {/* Content — centered, TV-scale */}
      <div className="relative z-10 h-screen flex flex-col items-center justify-center px-8 lg:px-16">
        {/* Top Row: Back + Status */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-8 lg:px-16 py-6">
          <button
            onClick={() => { stop(); navigate("/tv-app"); }}
            className={`flex items-center gap-3 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-lg font-semibold ${
              focusedBtn === "back"
                ? "bg-white text-[#0a0a0f] ring-4 ring-white/30"
                : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-arrow-left-line text-xl"></i>
            Volver
          </button>

          <div className="flex items-center gap-4">
            {/* Volume indicator */}
            {showVolume && (
              <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-white/10">
                <i className={`${volume === 0 ? "ri-volume-mute-line" : volume < 50 ? "ri-volume-down-line" : "ri-volume-up-line"} text-white text-lg`}></i>
                <div className="w-32 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-white rounded-full transition-all duration-200" style={{ width: `${volume}%` }}></div>
                </div>
                <span className="text-sm text-white font-semibold w-8 text-right">{volume}</span>
              </div>
            )}

            {/* Technical Status Badge — always visible */}
            {!isFallback && !hasStreamError && (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full border text-base font-bold ${
                techStatus === "active"
                  ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-400"
                  : techStatus === "offline" || techStatus === "not_configured"
                  ? "bg-amber-500/15 border-amber-500/30 text-amber-400"
                  : isTransient
                  ? "bg-sky-500/15 border-sky-500/30 text-sky-400"
                  : "bg-red-500/15 border-red-500/30 text-red-400"
              }`}>
                <i className={`${techIcon} text-lg ${isTransient ? "animate-spin" : ""}`}></i>
                <span>{techLabel}</span>
              </div>
            )}

            {/* Fallback badge */}
            {isFallback && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-rose-500/20 to-orange-500/20 border border-rose-500/30">
                <i className="ri-tv-line text-rose-400 text-lg"></i>
                <span className="text-base font-bold text-rose-400">RFW TV</span>
              </div>
            )}
          </div>
        </div>

        {/* Center: Station Info + Logo */}
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-5xl w-full">
          {/* Cover Art / Logo */}
          <div className="relative flex-shrink-0">
            <div className={`w-56 h-56 lg:w-72 lg:h-72 rounded-3xl overflow-hidden ring-4 ring-white/10 shadow-2xl ${isFallback ? "ring-rose-500/20" : ""}`}>
              <img src={displayStation.logoUrl} alt={displayStation.name} className="w-full h-full object-cover" />
            </div>
            {isPlaying && !isFallback && techStatus === "active" && (
              <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-end gap-1.5 h-8">
                {[4, 8, 6, 10, 5, 7, 9, 6].map((h, i) => (
                  <div
                    key={i}
                    className="w-1.5 rounded-full bg-accent-400"
                    style={{
                      height: `${h * 3}px`,
                      animation: `tv-eq 0.8s ease-in-out infinite`,
                      animationDelay: `${i * 0.1}s`,
                    }}
                  ></div>
                ))}
              </div>
            )}
            {isFallback && (
              <div className="absolute -inset-2 rounded-[32px] border border-rose-500/30 animate-pulse pointer-events-none"></div>
            )}
          </div>

          {/* Station Details */}
          <div className="text-center lg:text-left flex-1">
            {isFallback ? (
              <>
                <h1 className="text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">
                  RFW <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-orange-400">TV</span>
                </h1>
                <p className="text-xl text-white/50 mt-2">RadioFrecuenciaWeb</p>
                <div className="mt-6 px-6 py-5 rounded-2xl bg-gradient-to-r from-rose-500/10 to-orange-500/10 border border-rose-500/20">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-tv-line text-rose-400 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-rose-200 text-sm font-semibold mb-1.5">
                        {fallbackInfo?.originalStationName || displayStation.name} no está transmitiendo en este momento.
                      </p>
                      <p className="text-rose-400/70 text-xs leading-relaxed">
                        No te preocupes — te trajimos a <strong className="text-rose-300">RFW TV</strong>, el canal oficial de RadioFrecuenciaWeb. Disfrutá del mejor contenido mientras esperás que vuelva la transmisión.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight">{displayStation.name}</h1>
                <p className="text-xl text-white/60 mt-2">{displayStation.frequency}</p>
                <p className="text-sm text-white/50 mt-1">{displayStation.city}, {displayStation.country}</p>

                {/* Metadata + Live Indicator */}
                <div className="mt-6 space-y-3">
                  {techStatus === "active" && displayStation.currentMetadata && (
                    <div className="flex items-center gap-3 justify-center lg:justify-start">
                      <span className="w-2 h-2 rounded-full bg-accent-400"></span>
                      <p className="text-xl font-bold text-white">{displayStation.currentMetadata}</p>
                    </div>
                  )}
                  {techStatus !== "active" && (
                    <div className="px-4 py-3 rounded-xl bg-foreground-950/30 border border-white/5 max-w-md mx-auto lg:mx-0">
                      <p className="text-sm text-white/60">{techMessage}</p>
                    </div>
                  )}
                </div>

                {/* Current Program */}
                {displayProgram && (
                  <div className="mt-5 px-5 py-4 rounded-2xl bg-white/5 border border-white/5 max-w-md mx-auto lg:mx-0">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                        <i className="ri-mic-line text-emerald-400"></i>
                      </div>
                      <div className="text-left">
                        <p className="text-xs text-white/40 uppercase tracking-wide">Ahora en el aire</p>
                        <p className="text-base font-bold text-white">{displayProgram.name}</p>
                        <p className="text-sm text-white/60">{displayProgram.hosts[0]?.name || "Varios"} · {displayProgram.schedule}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="flex items-center gap-8 mt-6 justify-center lg:justify-start">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-white">{formatCount(displayStation.listeners)}</p>
                    <p className="text-sm text-white/50 mt-1">Oyentes</p>
                  </div>
                  <div className="w-px h-10 bg-white/10"></div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-accent-400">{displayStation.rating}</p>
                    <p className="text-sm text-white/50 mt-1">Rating</p>
                  </div>
                  <div className="w-px h-10 bg-white/10"></div>
                  <div className="text-center">
                    <p className="text-3xl font-bold text-accent-400">{displayStation.category}</p>
                    <p className="text-sm text-white/50 mt-1">Categoría</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Description */}
        {!isFallback && (
          <p className="text-base text-white/40 mt-8 max-w-2xl text-center line-clamp-2">{displayStation.description}</p>
        )}

        {/* Bottom Controls Bar */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center justify-center gap-3 lg:gap-4 px-6 lg:px-16 py-8">
          {/* Back */}
          <button
            onClick={() => { stop(); navigate("/tv-app"); }}
            className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${
              focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-arrow-left-line text-lg"></i>
            Volver
          </button>

          {/* Previous Station */}
          <button
            onClick={() => {
              const allLive = getLiveStations();
              if (allLive.length > 0 && currentStation) {
                const curIdx = allLive.findIndex((s) => s.id === currentStation.id);
                const prev = curIdx > 0 ? allLive[curIdx - 1] : allLive[allLive.length - 1];
                changeStation(prev);
              }
            }}
            className={`flex items-center gap-1 px-3 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
              focusedBtn === "prev" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-skip-back-line text-lg"></i>
          </button>

          {/* Play / Pause */}
          <button
            onClick={toggle}
            className={`flex items-center gap-3 px-10 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-lg font-bold ${
              focusedBtn === "play"
                ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105"
                : isPlaying && techStatus === "active"
                  ? "bg-white text-[#0a0a0f] hover:bg-white/90"
                  : "bg-white/80 text-[#0a0a0f] hover:bg-white"
            }`}
          >
            <i className={`text-xl ${isPlaying ? "ri-pause-fill" : "ri-play-fill"}`}></i>
            {isPlaying ? "Pausar" : "Reproducir"}
          </button>

          {/* Next Station */}
          <button
            onClick={() => {
              const allLive = getLiveStations();
              if (allLive.length > 0 && currentStation) {
                const curIdx = allLive.findIndex((s) => s.id === currentStation.id);
                const next = curIdx < allLive.length - 1 ? allLive[curIdx + 1] : allLive[0];
                changeStation(next);
              }
            }}
            className={`flex items-center gap-1 px-3 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
              focusedBtn === "next" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-skip-forward-line text-lg"></i>
          </button>

          {/* Favorite */}
          <button
            onClick={toggleFavorite}
            className={`flex items-center gap-1.5 px-4 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
              focusedBtn === "fav"
                ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105"
                : isFavorited
                  ? "bg-rose-500/20 text-rose-400 hover:bg-rose-500/30"
                  : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className={`text-lg ${isFavorited ? "ri-heart-fill" : "ri-heart-line"}`}></i>
          </button>

          {/* Radio Profile */}
          <button
            onClick={() => navigate(`/tv-app/radio/${displayStation.id}`)}
            className={`flex items-center gap-1.5 px-4 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-sm font-bold ${
              focusedBtn === "profile"
                ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105"
                : "bg-white/10 hover:bg-white/15 text-white"
            }`}
          >
            <i className="ri-external-link-line text-lg"></i>
          </button>
        </div>
      </div>

      {/* Equalizer keyframes via style tag */}
      <style>{`
        @keyframes tv-eq {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
}